import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Modal,
  PageHeader,
  Radio,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  useToast,
} from '@catering/ui';
import type { BadgeVariant } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Landmark } from 'lucide-react';
import { type JSX, useEffect, useRef, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import { TAX_CONDITIONS, TAX_CONDITION_LABELS, type TaxCondition } from '../../customers/api';
import { isValidTaxId, normalizeTaxId } from '../../customers/cuit';
import type { FiscalEnvironmentValue } from '../../invoices/api';
import { mapArcaConfigError } from './arca-config-api-errors';
import {
  type ArcaCertificateEnvironment,
  type CertificateStatus,
  type ConnectionTestResult,
  type FiscalConfig,
  generateFiscalCsr,
  getFiscalConfig,
  testArcaConnection,
  updateFiscalEnvironment,
  updateFiscalIssuer,
  uploadFiscalCertificate,
} from './arca-config.api';

type TabKey = 'emisor' | 'certificados' | 'ambiente' | 'conexion';

const TAB_LABELS: Record<TabKey, string> = {
  emisor: 'Datos del emisor',
  certificados: 'Certificados',
  ambiente: 'Ambiente',
  conexion: 'Probar conexión',
};

const CERTIFICATE_ENVIRONMENTS: readonly ArcaCertificateEnvironment[] = [
  'homologacion',
  'produccion',
];

const ENVIRONMENT_LABELS: Record<FiscalEnvironmentValue, string> = {
  simulado: 'Simulado',
  homologacion: 'Homologación',
  produccion: 'Producción',
};

const CERTIFICATE_BADGE: Record<CertificateStatus, { variant: BadgeVariant; label: string }> = {
  missing: { variant: 'neutral', label: 'Sin certificado' },
  active: { variant: 'success', label: 'Vigente' },
  'expiring-soon': { variant: 'warning', label: 'Vence pronto' },
  expired: { variant: 'danger', label: 'Vencido' },
};

const CONNECTION_CHECK_LABELS = {
  wsaa: 'Autenticación con ARCA',
  dummy: 'Servicio de facturación',
  ptosVenta: 'Puntos de venta autorizados',
  tiposIva: 'Alícuotas de IVA habilitadas',
} as const;

interface IssuerFormState {
  cuit: string;
  legalName: string;
  street: string;
  city: string;
  province: string;
  postalCode: string;
  taxCondition: TaxCondition;
  grossIncomeNumber: string;
  activityStartDate: string;
}

const EMPTY_ISSUER_FORM: IssuerFormState = {
  cuit: '',
  legalName: '',
  street: '',
  city: '',
  province: '',
  postalCode: '',
  taxCondition: 'RESPONSABLE_INSCRIPTO',
  grossIncomeNumber: '',
  activityStartDate: '',
};

function issuerFormFrom(config: FiscalConfig): IssuerFormState {
  if (config.issuer === null) {
    return EMPTY_ISSUER_FORM;
  }

  return {
    cuit: config.issuer.cuit,
    legalName: config.issuer.legalName,
    street: config.issuer.address.street,
    city: config.issuer.address.city,
    province: config.issuer.address.province,
    postalCode: config.issuer.address.postalCode,
    taxCondition: config.issuer.taxCondition,
    grossIncomeNumber: config.issuer.grossIncomeNumber,
    activityStartDate: config.issuer.activityStartDate,
  };
}

function daysUntil(isoDate: string): number {
  return Math.ceil((new Date(isoDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

function downloadTextFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('No pudimos leer el archivo.'));
    reader.readAsText(file);
  });
}

/**
 * `fiscal-configuration/spec.md`, "Pantalla de configuración fiscal en el
 * back-office" (`design.md` §11). Reachable only from the sidebar entry
 * gated on `fiscal.config` — same pattern as `FiscalPointOfSaleConfigPage`
 * — but this screen also double-checks the permission itself, because
 * `GET /fiscal-config` requires it on every route with no read exception
 * (`fiscal-config.controller.ts`): a user who bypasses the sidebar link
 * must never see the form flash open before the 403 arrives.
 */
export function ArcaConfigPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState<TabKey>('emisor');

  const canAccess = hasPermission(currentUser.data, 'fiscal.config');

  const query = useQuery({
    queryKey: ['fiscal-config'],
    queryFn: getFiscalConfig,
    staleTime: 0,
    enabled: canAccess,
  });

  function setConfig(updated: FiscalConfig): void {
    queryClient.setQueryData(['fiscal-config'], updated);
  }

  // Issuer form
  const [issuerForm, setIssuerForm] = useState<IssuerFormState>(EMPTY_ISSUER_FORM);
  const issuerLoaded = useRef(false);

  useEffect(() => {
    if (query.data !== undefined && !issuerLoaded.current) {
      setIssuerForm(issuerFormFrom(query.data));
      issuerLoaded.current = true;
    }
  }, [query.data]);

  const issuerMutation = useMutation({
    mutationFn: () =>
      updateFiscalIssuer({
        cuit: normalizeTaxId(issuerForm.cuit),
        legalName: issuerForm.legalName,
        address: {
          street: issuerForm.street,
          city: issuerForm.city,
          province: issuerForm.province,
          postalCode: issuerForm.postalCode,
        },
        taxCondition: issuerForm.taxCondition,
        grossIncomeNumber: issuerForm.grossIncomeNumber,
        activityStartDate: issuerForm.activityStartDate,
      }),
    onSuccess: (updated) => {
      setConfig(updated);
      toast('success', 'Guardado');
    },
  });

  // Certificates
  const [csrAlias, setCsrAlias] = useState<Record<ArcaCertificateEnvironment, string>>({
    homologacion: '',
    produccion: '',
  });
  const [generatedCsr, setGeneratedCsr] = useState<
    Partial<Record<ArcaCertificateEnvironment, string>>
  >({});

  const csrMutation = useMutation({
    mutationFn: (environment: ArcaCertificateEnvironment) =>
      generateFiscalCsr(environment, csrAlias[environment]),
    onSuccess: (result, environment) => {
      setGeneratedCsr((current) => ({ ...current, [environment]: result.csrPem }));
      toast('success', 'Solicitud de certificado generada');
      void queryClient.invalidateQueries({ queryKey: ['fiscal-config'] });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({
      environment,
      certificatePem,
    }: {
      environment: ArcaCertificateEnvironment;
      certificatePem: string;
    }) => uploadFiscalCertificate(environment, certificatePem),
    onSuccess: (updated) => {
      setConfig(updated);
      toast('success', 'Certificado guardado');
    },
  });

  async function handleUploadCertificate(
    environment: ArcaCertificateEnvironment,
    file: File,
  ): Promise<void> {
    const certificatePem = await readFileAsText(file);
    uploadMutation.mutate({ environment, certificatePem });
  }

  // Environment / Producción guardrail
  const [productionModalOpen, setProductionModalOpen] = useState(false);
  const [cuitConfirmation, setCuitConfirmation] = useState('');

  const environmentMutation = useMutation({
    mutationFn: (input: { environment: FiscalEnvironmentValue; cuitConfirmation?: string }) =>
      updateFiscalEnvironment(input.environment, input.cuitConfirmation),
    onSuccess: (updated) => {
      setConfig(updated);
      toast('success', 'Guardado');
      setProductionModalOpen(false);
      setCuitConfirmation('');
    },
  });

  function handleSelectEnvironment(environment: FiscalEnvironmentValue): void {
    if (environment === 'produccion') {
      environmentMutation.reset();
      setCuitConfirmation('');
      setProductionModalOpen(true);
      return;
    }
    environmentMutation.mutate({ environment });
  }

  // Probar conexión
  const [testEnvironment, setTestEnvironment] =
    useState<ArcaCertificateEnvironment>('homologacion');
  const [testResult, setTestResult] = useState<ConnectionTestResult | undefined>(undefined);

  const testConnectionMutation = useMutation({
    mutationFn: () => testArcaConnection(testEnvironment),
    onSuccess: (result) => {
      setTestResult(result);
      void queryClient.invalidateQueries({ queryKey: ['fiscal-config'] });
    },
  });

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canAccess) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a la configuración fiscal.
      </Alert>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Landmark}
        title="Configuración fiscal"
        description="Datos del emisor, ambiente, certificados y conexión ante ARCA."
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar la configuración fiscal">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <>
          <FiscalSteps checklist={query.data.checklist} onContinue={setTab} />

          <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
            <TabList aria-label="Pestañas de configuración fiscal">
              {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
                <Tab key={key} value={key}>
                  {TAB_LABELS[key]}
                </Tab>
              ))}
            </TabList>

            <TabPanel value="emisor">
              <div className="flex flex-col gap-4">
                {issuerMutation.isError ? (
                  <Alert tone="danger">{mapArcaConfigError(issuerMutation.error)}</Alert>
                ) : null}

                <Field
                  label="CUIT"
                  name="cuit"
                  required
                  value={issuerForm.cuit}
                  onChange={(event) => setIssuerForm((f) => ({ ...f, cuit: event.target.value }))}
                  {...(issuerForm.cuit !== '' && !isValidTaxId(normalizeTaxId(issuerForm.cuit))
                    ? { error: 'El CUIT no tiene un dígito verificador válido.' }
                    : {})}
                />
                <Field
                  label="Razón social"
                  name="legalName"
                  required
                  value={issuerForm.legalName}
                  onChange={(event) =>
                    setIssuerForm((f) => ({ ...f, legalName: event.target.value }))
                  }
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Domicilio"
                    name="street"
                    required
                    value={issuerForm.street}
                    onChange={(event) =>
                      setIssuerForm((f) => ({ ...f, street: event.target.value }))
                    }
                  />
                  <Field
                    label="Ciudad"
                    name="city"
                    required
                    value={issuerForm.city}
                    onChange={(event) => setIssuerForm((f) => ({ ...f, city: event.target.value }))}
                  />
                  <Field
                    label="Provincia"
                    name="province"
                    required
                    value={issuerForm.province}
                    onChange={(event) =>
                      setIssuerForm((f) => ({ ...f, province: event.target.value }))
                    }
                  />
                  <Field
                    label="Código postal"
                    name="postalCode"
                    required
                    value={issuerForm.postalCode}
                    onChange={(event) =>
                      setIssuerForm((f) => ({ ...f, postalCode: event.target.value }))
                    }
                  />
                </div>

                <fieldset className="flex flex-col gap-2">
                  <legend className="text-sm font-medium text-slate-900">
                    Condición frente al IVA
                  </legend>
                  {TAX_CONDITIONS.map((condition) => (
                    <Radio
                      key={condition}
                      label={TAX_CONDITION_LABELS[condition]}
                      name="taxCondition"
                      checked={issuerForm.taxCondition === condition}
                      onChange={() => setIssuerForm((f) => ({ ...f, taxCondition: condition }))}
                    />
                  ))}
                </fieldset>

                <Field
                  label="Ingresos Brutos"
                  name="grossIncomeNumber"
                  required
                  value={issuerForm.grossIncomeNumber}
                  onChange={(event) =>
                    setIssuerForm((f) => ({ ...f, grossIncomeNumber: event.target.value }))
                  }
                />
                <Field
                  label="Inicio de actividades"
                  name="activityStartDate"
                  type="date"
                  required
                  value={issuerForm.activityStartDate}
                  onChange={(event) =>
                    setIssuerForm((f) => ({ ...f, activityStartDate: event.target.value }))
                  }
                />

                <div>
                  <Button
                    loading={issuerMutation.isPending}
                    onClick={() => issuerMutation.mutate()}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="certificados">
              <div className="flex flex-col gap-6">
                {uploadMutation.isError ? (
                  <Alert tone="danger">{mapArcaConfigError(uploadMutation.error)}</Alert>
                ) : null}
                {csrMutation.isError ? (
                  <Alert tone="danger">{mapArcaConfigError(csrMutation.error)}</Alert>
                ) : null}

                {CERTIFICATE_ENVIRONMENTS.map((environment) => {
                  const certificate = query.data.certificates[environment];
                  const status = query.data.checklist.certificateStatus[environment];
                  const badge = CERTIFICATE_BADGE[status];

                  return (
                    <Card key={environment}>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-900">
                            {ENVIRONMENT_LABELS[environment]}
                          </h3>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </div>

                        <p className="text-xs text-slate-500">
                          {environment === 'homologacion'
                            ? 'Presentá el CSR ante WSASS en Homologación, cargá el .crt que te entrega ARCA y autorizá el servicio wsfe.'
                            : 'Presentá el CSR en el Administrador de Certificados Digitales de Producción, cargá el .crt y autorizá el servicio wsfe.'}
                        </p>

                        <Field
                          label="Alias"
                          name={`alias-${environment}`}
                          value={csrAlias[environment]}
                          onChange={(event) =>
                            setCsrAlias((current) => ({
                              ...current,
                              [environment]: event.target.value,
                            }))
                          }
                        />

                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            variant="secondary"
                            disabled={
                              !query.data.checklist.issuerDataComplete ||
                              csrAlias[environment].trim() === ''
                            }
                            loading={csrMutation.isPending && csrMutation.variables === environment}
                            onClick={() => csrMutation.mutate(environment)}
                          >
                            Generar clave y CSR
                          </Button>

                          {generatedCsr[environment] !== undefined ? (
                            <Button
                              variant="secondary"
                              onClick={() =>
                                downloadTextFile(
                                  generatedCsr[environment] as string,
                                  `arca-${environment}.csr`,
                                )
                              }
                            >
                              Descargar CSR
                            </Button>
                          ) : null}

                          <label className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-slate-900">
                              Cargar certificado (.crt)
                            </span>
                            <input
                              type="file"
                              accept=".crt,.pem"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file !== undefined) {
                                  void handleUploadCertificate(environment, file);
                                }
                                event.target.value = '';
                              }}
                            />
                          </label>
                        </div>

                        {certificate !== null ? (
                          <dl className="grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
                            <div>
                              <dt className="font-medium text-slate-900">Subject</dt>
                              <dd>{certificate.subject ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="font-medium text-slate-900">Vencimiento</dt>
                              <dd>
                                {certificate.notAfter === null
                                  ? '—'
                                  : `${certificate.notAfter.slice(0, 10)}${
                                      status === 'expiring-soon'
                                        ? ` (vence en ${daysUntil(certificate.notAfter)} días)`
                                        : ''
                                    }`}
                              </dd>
                            </div>
                          </dl>
                        ) : null}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </TabPanel>

            <TabPanel value="ambiente">
              <div className="flex flex-col gap-4">
                {environmentMutation.isError && !productionModalOpen ? (
                  <Alert tone="danger">{mapArcaConfigError(environmentMutation.error)}</Alert>
                ) : null}

                <fieldset className="flex flex-col gap-2">
                  <legend className="text-sm font-medium text-slate-900">Ambiente activo</legend>
                  {(['simulado', 'homologacion', 'produccion'] as const).map((environment) => (
                    <Radio
                      key={environment}
                      label={ENVIRONMENT_LABELS[environment]}
                      name="environment"
                      checked={query.data.environment === environment}
                      onChange={() => handleSelectEnvironment(environment)}
                    />
                  ))}
                </fieldset>
              </div>
            </TabPanel>

            <TabPanel value="conexion">
              <div className="flex flex-col gap-4">
                {testConnectionMutation.isError ? (
                  <Alert tone="danger">{mapArcaConfigError(testConnectionMutation.error)}</Alert>
                ) : null}

                <fieldset className="flex items-center gap-4">
                  <legend className="sr-only">Ambiente a probar</legend>
                  {CERTIFICATE_ENVIRONMENTS.map((environment) => (
                    <Radio
                      key={environment}
                      label={ENVIRONMENT_LABELS[environment]}
                      name="testEnvironment"
                      checked={testEnvironment === environment}
                      onChange={() => setTestEnvironment(environment)}
                    />
                  ))}
                </fieldset>

                <div>
                  <Button
                    loading={testConnectionMutation.isPending}
                    onClick={() => testConnectionMutation.mutate()}
                  >
                    Probar conexión
                  </Button>
                </div>

                {testResult !== undefined ? (
                  <dl className="flex flex-col gap-2">
                    {(
                      Object.keys(
                        CONNECTION_CHECK_LABELS,
                      ) as (keyof typeof CONNECTION_CHECK_LABELS)[]
                    ).map((key) => (
                      <div key={key} className="flex items-center justify-between gap-4 text-sm">
                        <dt className="font-medium text-slate-900">
                          {CONNECTION_CHECK_LABELS[key]}
                        </dt>
                        <dd className="flex items-center gap-2">
                          <Badge
                            variant={
                              testResult[key].ok === true
                                ? 'success'
                                : testResult[key].ok === false
                                  ? 'danger'
                                  : 'neutral'
                            }
                          >
                            {testResult[key].ok === true
                              ? 'OK'
                              : testResult[key].ok === false
                                ? 'Error'
                                : 'No realizado'}
                          </Badge>
                          <span className="text-slate-600">{testResult[key].detail}</span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}

                <Card>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-slate-600">
                      La numeración y los tipos de comprobante habilitados se configuran en Puntos
                      de venta.
                    </span>
                    <Link to="/settings/fiscal-points-of-sale" className="underline">
                      Ir a Puntos de venta
                    </Link>
                  </div>
                </Card>
              </div>
            </TabPanel>
          </Tabs>
        </>
      ) : null}

      {productionModalOpen ? (
        <Modal
          open
          onClose={() => setProductionModalOpen(false)}
          title="Confirmar cambio a Producción"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {environmentMutation.isError ? (
              <Alert tone="danger">{mapArcaConfigError(environmentMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-600">
              Este cambio habilita la emisión de comprobantes reales ante ARCA. Para confirmar,
              escribí el CUIT del emisor exactamente como está configurado.
            </p>
            <Field
              label="CUIT del emisor"
              name="cuitConfirmation"
              autoComplete="off"
              value={cuitConfirmation}
              onChange={(event) => setCuitConfirmation(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={environmentMutation.isPending}
                disabled={normalizeTaxId(cuitConfirmation) !== (query.data?.issuer?.cuit ?? '')}
                onClick={() =>
                  environmentMutation.mutate({
                    environment: 'produccion',
                    cuitConfirmation: normalizeTaxId(cuitConfirmation),
                  })
                }
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setProductionModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

interface FiscalStepDefinition {
  label: string;
  tab: TabKey;
  done: boolean;
}

function fiscalStepsFrom(checklist: FiscalConfig['checklist']): FiscalStepDefinition[] {
  return [
    { label: 'Datos del emisor', tab: 'emisor', done: checklist.issuerDataComplete },
    {
      label: 'Certificado de Homologación',
      tab: 'certificados',
      done: checklist.certificateStatus.homologacion === 'active',
    },
    {
      label: 'Prueba de conexión en Homologación',
      tab: 'conexion',
      done: checklist.lastConnectionTest.homologacion.success === true,
    },
    {
      label: 'Certificado de Producción',
      tab: 'certificados',
      done: checklist.certificateStatus.produccion === 'active',
    },
    {
      label: 'Prueba de conexión en Producción',
      tab: 'conexion',
      done: checklist.lastConnectionTest.produccion.success === true,
    },
  ];
}

function FiscalSteps({
  checklist,
  onContinue,
}: {
  checklist: FiscalConfig['checklist'];
  onContinue: (tab: TabKey) => void;
}): JSX.Element {
  const steps = fiscalStepsFrom(checklist);
  const doneCount = steps.filter((step) => step.done).length;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Pasos para facturar con ARCA</h2>
        <span className="text-xs font-medium text-slate-500">{`${doneCount} de ${steps.length} listos`}</span>
      </div>
      <ol aria-label="Pasos para facturar con ARCA" className="mt-2 flex flex-col gap-2">
        {steps.map((step, index) => (
          <li key={`${step.label}`} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-3">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                {index + 1}
              </span>
              <span className="text-slate-600">{step.label}</span>
            </span>
            <span className="flex items-center gap-2">
              <Badge variant={step.done ? 'success' : 'neutral'}>
                {step.done ? 'Completo' : 'Pendiente'}
              </Badge>
              {!step.done ? (
                <Button variant="secondary" size="sm" onClick={() => onContinue(step.tab)}>
                  Continuar
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}
